// Clipboard paste helpers for the booking "Calc" price sheet.
//
// Excel / Google Sheets put copied cells on the clipboard as TSV: columns
// separated by tabs, rows by newlines, plus a trailing newline. These helpers
// turn that text into a grid and spill it into the sparse cell map starting at
// a focused anchor cell, clipping anything past the A-G / row-20 grid bounds.

export const COLS = ["A", "B", "C", "D", "E", "F", "G"];
export const MAX_ROW = 20;

// TSV text -> 2D array of raw cell strings.
export function parseClipboardGrid(text) {
  const normalized = String(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalized.split("\n");
  // Excel appends one trailing newline; drop the empty line it produces.
  if (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
  return lines.map((line) => line.split("\t"));
}

// True when the clipboard holds more than one cell (so we should intercept the
// paste). A lone value with a trailing newline still counts as a single cell.
export function isMultiCell(text) {
  const withoutTrailingNewline = String(text).replace(/\r?\n$/, "");
  return /[\t\n\r]/.test(withoutTrailingNewline);
}

// "B2" -> { col: 1, row: 2 } using the sheet's own A-G column order; null if
// the key is outside the grid.
export function parseCell(key) {
  const m = /^([A-G])(\d+)$/.exec(key || "");
  if (!m) return null;
  return { col: COLS.indexOf(m[1]), row: Number(m[2]) };
}

// Normalized rectangle spanning two cells, as column/row index bounds.
export function rangeBounds(a, b) {
  const pa = parseCell(a);
  const pb = parseCell(b);
  if (!pa || !pb) return null;
  return {
    colLo: Math.min(pa.col, pb.col),
    colHi: Math.max(pa.col, pb.col),
    rowLo: Math.min(pa.row, pb.row),
    rowHi: Math.max(pa.row, pb.row),
  };
}

// Formula reference text for a drag from `a` to `b`: a single ref ("B2") when
// both ends are the same cell, otherwise a range ("A1:C3") top-left:bottom-right.
export function cellRange(a, b) {
  const bnd = rangeBounds(a, b);
  if (!bnd) return "";
  const topLeft = COLS[bnd.colLo] + bnd.rowLo;
  if (bnd.colLo === bnd.colHi && bnd.rowLo === bnd.rowHi) return topLeft;
  return topLeft + ":" + COLS[bnd.colHi] + bnd.rowHi;
}

// Excel copies thousand-grouped numbers as text ("1,000" / "1,234,567.89").
// Strip the grouping commas so they land as real numbers, not #ERROR. Anything
// that isn't a clean grouped number is left exactly as pasted.
const THOUSANDS_RE = /^-?\d{1,3}(,\d{3})+(\.\d+)?$/;
export function normalizePastedValue(raw) {
  const trimmed = String(raw).trim();
  if (THOUSANDS_RE.test(trimmed)) return trimmed.replace(/,/g, "");
  return raw;
}

// Build TSV (tab/newline) for a rectangular block of computed values so it can
// be pasted straight into Excel. Numbers are emitted plain ("1000000", not
// "1.000.000") so Excel parses them; empty cells become empty strings.
export function buildTSV(values, bounds) {
  const lines = [];
  for (let r = bounds.rowLo; r <= bounds.rowHi; r++) {
    const row = [];
    for (let c = bounds.colLo; c <= bounds.colHi; c++) {
      const v = values[COLS[c] + r];
      row.push(v === undefined || v === null ? "" : String(v));
    }
    lines.push(row.join("\t"));
  }
  return lines.join("\n");
}

// Spill `grid` into `cells` anchored at `anchorKey` (e.g. "B2"), returning the
// next cell map and how many cells fell outside the grid (0 if none).
export function applyPaste(cells, anchorKey, grid) {
  const m = /^([A-G])(\d+)$/.exec(anchorKey || "");
  if (!m) return { cells, clipped: 0 };

  const anchorCol = COLS.indexOf(m[1]);
  const anchorRow = Number(m[2]);
  const next = { ...cells };
  let clipped = 0;

  for (let i = 0; i < grid.length; i++) {
    for (let j = 0; j < grid[i].length; j++) {
      const col = anchorCol + j;
      const row = anchorRow + i;
      if (col >= COLS.length || row > MAX_ROW) {
        clipped += 1;
        continue;
      }
      const key = COLS[col] + row;
      const raw = normalizePastedValue(grid[i][j]);
      if (raw === "") delete next[key];
      else next[key] = { raw };
    }
  }

  return { cells: next, clipped };
}
